'use client';
import { createContext, useContext, type ReactNode } from 'react';
import type { TrendPeek } from '@/lib/stockTrend';

/* Carries the server-rendered <StockSnapshot> element down to the client
   components that show it (the Market tab, the loading shell, the closed
   mobile drawer) without threading a prop through every per-model map. Null
   outside a stock page or when the model has no rows. */
/** What the collapsed mobile drawer shows on its one line (StockPeekLine). */
export interface StockPeekData {
  /** Plain-text lead, e.g. "238 on dealer lots today · 952 listed". */
  headline: string;
  /** Cars on dealer lots (0 when the tracker has no status) and total listed. */
  onLot: number;
  total: number;
  /** Supply buckets for the mini bar — same keys/colours as the snapshot. */
  supply: { key: string; count: number }[];
  /** Sparkline + verdict; null for models without trend history. */
  trend: TrendPeek | null;
  /** Time-on-lot tag, e.g. "Steady but slowing"; null without enough history. */
  pace: string | null;
}

const SnapshotContext = createContext<{
  node: ReactNode;
  peek: StockPeekData | null;
  discord: ReactNode;
}>({ node: null, peek: null, discord: null });

export function StockSnapshotProvider({
  snapshot,
  peek = null,
  discord = null,
  children,
}: {
  snapshot: ReactNode;
  peek?: StockPeekData | null;
  /** Server-rendered Discord strip for the bottom of the pane / collapsed
   *  drawer (DiscordCTA variant="strip"). */
  discord?: ReactNode;
  children: ReactNode;
}) {
  return (
    <SnapshotContext.Provider value={{ node: snapshot, peek, discord }}>
      {children}
    </SnapshotContext.Provider>
  );
}

export const useStockSnapshot = (): ReactNode =>
  useContext(SnapshotContext).node;
export const useStockPeek = (): StockPeekData | null =>
  useContext(SnapshotContext).peek;
export const useStockDiscord = (): ReactNode =>
  useContext(SnapshotContext).discord;

/* What the map lets the snapshot do to its filters. Provided by InventoryMap
   (which owns the filter state); read by the Market tab, which turns clicks on
   the snapshot's supply segments and trim rows (and the colour bar list's rows)
   into filter toggles. Each
   `can*` is false when the model has no filter that matches that item. */
export interface SnapshotFilterControl {
  canStatus(bucket: string): boolean;
  isStatusActive(bucket: string): boolean;
  toggleStatus(bucket: string): void;
  canTrim(slug: string): boolean;
  isTrimActive(slug: string): boolean;
  toggleTrim(slug: string): void;
  /** Colours follow the filter bar's click rule (colorBar.ts `pickColor`):
   *  the first click shows only that colour, later clicks add or remove one,
   *  and removing the last brings every colour back. */
  canColor(name: string): boolean;
  /** True when this colour is currently shown on the map. */
  isColorOn(name: string): boolean;
  /** True when at least one colour is switched off. */
  colorFiltered: boolean;
  pickColor(name: string): void;
}

const ControlContext = createContext<SnapshotFilterControl | null>(null);
export const SnapshotFilterProvider = ControlContext.Provider;
export const useSnapshotFilterControl = (): SnapshotFilterControl | null =>
  useContext(ControlContext);
